import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, Form, Col, FormControl } from "react-bootstrap";
import Select from "../../components/ui/AppReactSelect";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import DOMPurify from "dompurify";

import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { createListing } from "../../actions/listingsActions";
import { toast } from "react-toastify";
import { AppAsyncSelect as AsyncSelect } from "../../components/ui/AppReactSelect";
import CategorySuggestions from "../../components/catalog/CategorySuggestions";
import { categoryPickerOptions, unitSelectOptions } from "../../utilities/catalogTaxonomy";
import { getSiUnits } from "../../actions/siUnitActions";
import { uploadImageToCloudinary as sharedUpload } from "../../utilities/imageUpload";
import ImageUploadStyledAlert from "views/Components/ImageUploadStyledAlert";
import { getUserFromLocalStorage } from "utilities/user";

const ListForm = () => {
  const { siUnits, loading } = useSelector((state) => state.siUnits);
  const [selectedUnit, setSelectedUnit] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadDone, setUploadDone] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const user = getUserFromLocalStorage();
  const [selectedCategory, setSelectedCategory] = useState([]);
  const [inputValue, setValue] = useState("");
  // const [inputFileValue, setInputFileValue] = useState("");
  const [uploadedFileName, setUploadedFileName] = useState("");
  // Every category, kept for the suggestions to resolve their picks against.
  const [allCategories, setAllCategories] = useState([]);

  //Return policy
  const [useDefaultPolicy, setUseDefaultPolicy] = useState(true);
  const [customPolicy, setCustomPolicy] = useState("");

  // Alert
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [description, setDescription] = useState("");

  const [postData, setPostData] = useState({
    title: "",
    // description: "",
    price: "",
    countInStock: "",
    measurements: "",
    //  category: "",
    // categories: [],
    vendorName: "",
    vendorContactNumber: "",
    vendorAddress: "",
    pic: "",
  });

  const [image, setImage] = useState("");

  const dispatch = useDispatch();
  const history = useHistory();

  const clear = () => {
    setPostData({
      title: "",
      // description: "",
      price: "",
      countInStock: "",
      measurements: "",
      pic: "",
    }),
      setDescription("");
    setSelectedCategory([]);
    setSelectedUnit([]);
    setCustomPolicy("");
    setDescription("");
    setImage("");
    setIsUploading(false);
    setUploadDone(false);
    setUploadProgress(0);
    setUploadedFileName("");
  };

  useEffect(() => {
    dispatch(getSiUnits());
  }, []);

  /* The unit picker reads `siUnits` directly through unitSelectOptions(),
     which groups by family and leaves plural duplicates out. */

  const fetchImageToUpload = useCallback(() => {
    return new Promise(async (resolve, reject) => {
      setIsUploading(true);

      try {
        const result = await sharedUpload(image, {
          vendorMedia: {
            vendorType: user?.result?.operationMode || "unknown",
            vendorId: user?.result?._id,
            assetPurpose: "product_cover",
            entityType: "listing",
            entityId: postData.title || "new-listing",
            source: "dashboard",
          },
          onProgress: (percent) => setUploadProgress(percent),
        });

        setImage(result.secure_url);
        setIsUploading(false);
        setUploadDone(true);

        const abortController = new AbortController();
        resolve(abortController);
      } catch (err) {
        setIsUploading(false);
        reject(err);
      }
    });
  }, [image, postData.title, user?.result?._id, user?.result?.operationMode]);

  useEffect(() => {
    let active = true;
    let abortControllerPromise;

    if (image?.name) {
      if (active) {
        abortControllerPromise = fetchImageToUpload();
      }
    }
    return () => {
      active = false;
      if (abortControllerPromise) {
        abortControllerPromise.then((abortController) => {
          abortController.abort();
        });
      }
    };
  }, [image?.name, fetchImageToUpload]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (image?.name && !uploadDone) {
      const abortController = await fetchImageToUpload();
      if (abortController && !abortController.signal.aborted) {
        abortController.abort();
      }
    }

    // Sanitize customPolicy text
    const sanitizedCustomPolicy = DOMPurify.sanitize(customPolicy);

    dispatch(
      createListing(
        {
          ...postData,
          pic: image,
          name: user?.result?.name,
          category: selectedCategory,
          unit_id: selectedUnit.value,
          description: description, // Use the Quill editor content
          useDefaultPolicy: useDefaultPolicy, // Make sure to include this
          customPolicy: sanitizedCustomPolicy,
        },
        user?.result?._id
      )
    );
    clear();

    history.push("/admin/shopCards");
  };

  const loadCategory = async (inputValue) => {
    const res = await fetch("/category/get-all");
    const categories = await res.json();
    if (Array.isArray(categories)) setAllCategories(categories);
    // Grouped, "All"/"Random"/inactive left out, aliases and keywords searchable.
    return categoryPickerOptions(Array.isArray(categories) ? categories : [], {
      input: inputValue,
      selected: selectedCategory,
    });
  };

  // handle selection
  const handleInputChange = (value) => {
    setValue(value);
  };

  // handle Client Selection
  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
  };

  // handle Unit Selection
  const handleUnitChange = (value) => {
    setSelectedUnit(value);
  };

  // Check if file is JPEG
  const isJPEG = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const buffer = e.target.result;
        const view = new DataView(buffer);
        const isJPEG =
          view.byteLength >= 2 && view.getUint16(0, false) === 0xffd8; // Check if the file starts with a JPEG SOI marker (0xFFD8)
        resolve(isJPEG);
      };
      reader.onerror = (e) => {
        reject(e);
      };
      reader.readAsArrayBuffer(file);
    });
  };

  // Check if file is a PNG
  const isPNG = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const buffer = e.target.result;
        const view = new DataView(buffer);
        const pngSignature = [137, 80, 78, 71, 13, 10, 26, 10];
        const isPNG = pngSignature.every(
          (byte, index) => view.getUint8(index) === byte
        );
        resolve(isPNG);
      };
      reader.onerror = (e) => {
        reject(e);
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    const isFileJPEG = await isJPEG(file);
    const isFilePNG = await isPNG(file);
    const fileSizeInMB = file.size / (1024 * 1024);

    if (!isFileJPEG && !isFilePNG) {
      setSnackbarSeverity("error");
      setSnackbarMessage(
        "Invalid file type. Only JPG, JPEG, and PNG files are allowed."
      );
      setOpenSnackbar(true);
      setUploadedFileName(""); // Clear the uploaded file name
      return;
    }

    if (fileSizeInMB > 10) {
      setSnackbarSeverity("error");
      setSnackbarMessage(
        "File size exceeds 10 MB. Please choose a smaller image."
      );
      setOpenSnackbar(true);
      setUploadedFileName(""); // Clear the uploaded file name
      return;
    }

    // Continue with the upload process
    setImage(file);
    setUploadedFileName(file.name); // Set the uploaded file name
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  const handleDescriptionChange = (value) => {
    setDescription(value);
  };

  //React Quill modules
  const modules = {
    toolbar: [
      ["bold", "italic", "underline", "strike"],
      ["blockquote", "code-block"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ size: ["small", false, "large", "huge"] }],
      [{ color: [] }, { background: [] }],
      ["clean"],
    ],
  };
  const fieldStyle = { marginBottom: "12px" };
  const labelStyle = { fontSize: "0.8rem", fontWeight: 600, marginBottom: "3px", display: "block", color: "#444" };

  return (
    <>
      <ImageUploadStyledAlert
        open={openSnackbar}
        handleClose={handleCloseSnackbar}
        severity={snackbarSeverity}
        message={snackbarMessage}
      />

      <Col md="10">
        <Form
          action=""
          id="RegisterValidation"
          autoComplete="off"
          method=""
          onSubmit={handleSubmit}
        >
          <Card>
            <Card.Header style={{ paddingBottom: "8px" }}>
              <Card.Title as="h4" style={{ marginBottom: "2px" }}>Listing Form</Card.Title>
              <p className="card-category" style={{ marginBottom: 0 }}>Products ~ Create Listing.</p>
            </Card.Header>
            <Card.Body style={{ padding: "16px 20px" }}>
              {/* Row 1: Name + Price + Stock */}
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: "12px", marginBottom: "12px" }}>
                <div>
                  <label style={labelStyle}>1. Product Name <span className="star">*</span></label>
                  <Form.Control
                    size="sm"
                    name="title"
                    type="text"
                    value={postData.title}
                    maxLength="40"
                    placeholder="Product name (max 40 chars)"
                    onChange={(e) => setPostData({ ...postData, title: e.target.value })}
                  />
                  <small style={{ fontSize: "0.7rem", color: "#888" }}>{postData.title.length}/40</small>
                </div>
                <div>
                  <label style={labelStyle}>3. Item Price <span className="star">*</span></label>
                  <Form.Control
                    size="sm"
                    name="price"
                    type="text"
                    pattern="[0-9]*"
                    placeholder="Price"
                    value={postData.price}
                    onChange={(e) => setPostData({ ...postData, price: e.target.value })}
                  />
                </div>
                <div>
                  <label style={labelStyle}>4. In Stock <span className="star">*</span></label>
                  <Form.Control
                    size="sm"
                    name="countInStock"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="Qty"
                    value={postData.countInStock}
                    onChange={(e) => setPostData({ ...postData, countInStock: e.target.value })}
                  />
                </div>
              </div>

              {/* Row 2: Category + Measurements + Unit */}
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: "12px", marginBottom: "12px" }}>
                <div>
                  <label style={labelStyle}>5. Category <span className="star">*</span></label>
                  <AsyncSelect
                    cacheOptions
                    defaultOptions
                    value={selectedCategory}
                    getOptionLabel={(e) => e.category}
                    getOptionValue={(e) => e._id}
                    loadOptions={loadCategory}
                    onInputChange={handleInputChange}
                    onChange={handleCategoryChange}
                    isClearable={true}
                    isMulti={true}
                    placeholder="Search categories..."
                    styles={{ control: (b) => ({ ...b, minHeight: "31px", fontSize: "0.875rem" }) }}
                  />
                  <CategorySuggestions
                    name={postData.title}
                    unitId={selectedUnit?.value || null}
                    vendorId={user?.result?._id}
                    categories={allCategories}
                    selected={selectedCategory}
                    onAdd={(category) => setSelectedCategory((prev) => [...(prev || []), category])}
                    onApplyUnit={({ _id, unit, measurements }) => {
                      setSelectedUnit({ value: _id, label: unit, name: unit });
                      setPostData((prev) => ({ ...prev, measurements }));
                    }}
                  />
                </div>
                <div>
                  <label style={labelStyle}>6. Measurements <span className="star">*</span></label>
                  <Form.Control
                    size="sm"
                    name="measurements"
                    type="text"
                    inputMode="numeric"
                    pattern="^\d*(\.\d{0,2})?$"
                    placeholder="e.g. 1.5"
                    value={postData.measurements}
                    onChange={(e) => setPostData({ ...postData, measurements: e.target.value })}
                  />
                </div>
                <div>
                  <label style={labelStyle}>10. Unit <span className="star">*</span></label>
                  <Select
                    options={unitSelectOptions(siUnits, selectedUnit?.value)}
                    value={selectedUnit?.value ? selectedUnit : null}
                    onChange={handleUnitChange}
                    isClearable={true}
                    isSearchable={true}
                    placeholder="Unit..."
                    styles={{ control: (b) => ({ ...b, minHeight: "31px", fontSize: "0.875rem" }) }}
                  />
                </div>
              </div>

              {/* Description */}
              <div style={fieldStyle}>
                <label style={labelStyle}>2. Product Description <span className="star">*</span></label>
                <ReactQuill
                  value={description}
                  onChange={handleDescriptionChange}
                  modules={modules}
                  formats={["bold","italic","underline","strike","blockquote","list","bullet","size","color","background"]}
                  style={{ height: "160px", marginBottom: "42px" }}
                  placeholder="Enter product description here..."
                />
              </div>

              {/* Vendor info row */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "12px" }}>
                <div>
                  <label style={labelStyle}>7. Vendor Name</label>
                  <Form.Control
                    size="sm"
                    type="text"
                    value={postData.vendorName}
                    placeholder="Vendor name"
                    onChange={(e) => setPostData({ ...postData, vendorName: e.target.value })}
                  />
                </div>
                <div>
                  <label style={labelStyle}>8. Contact Number</label>
                  <Form.Control
                    size="sm"
                    type="text"
                    value={postData.vendorContactNumber}
                    placeholder="Contact number"
                    onChange={(e) => setPostData({ ...postData, vendorContactNumber: e.target.value })}
                  />
                </div>
                <div>
                  <label style={labelStyle}>9. Physical Address</label>
                  <Form.Control
                    size="sm"
                    type="text"
                    value={postData.vendorAddress}
                    placeholder="Vendor address"
                    onChange={(e) => setPostData({ ...postData, vendorAddress: e.target.value })}
                  />
                </div>
              </div>

              {/* Return Policy + Image upload row */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "8px" }}>
                <div style={{ border: "1px solid #e9ecef", borderRadius: "4px", padding: "10px" }}>
                  <div style={{ display: "flex", alignItems: "center", marginBottom: "6px" }}>
                    <input
                      type="checkbox"
                      id="default-return-policy-checkbox"
                      checked={useDefaultPolicy}
                      onChange={(e) => setUseDefaultPolicy(e.target.checked)}
                      style={{ marginRight: "6px" }}
                    />
                    <label htmlFor="default-return-policy-checkbox" style={{ fontSize: "0.8rem", margin: 0 }}>
                      Use default return policy
                    </label>
                  </div>
                  {useDefaultPolicy && (
                    <small style={{ color: "green", fontStyle: "italic", fontSize: "0.7rem" }}>
                      Default return policy will be used.
                    </small>
                  )}
                  {!useDefaultPolicy && (
                    <>
                      <FormControl
                        as="textarea"
                        rows={3}
                        size="sm"
                        placeholder="Enter custom return policy..."
                        style={{ borderColor: "orange", fontSize: "0.8rem" }}
                        value={customPolicy}
                        onChange={(e) => setCustomPolicy(e.target.value)}
                        required={!useDefaultPolicy}
                      />
                      <small style={{ color: "orange", fontSize: "0.7rem" }}>Custom return policy required.</small>
                    </>
                  )}
                </div>

                <div style={{ border: "1px solid #e9ecef", borderRadius: "4px", padding: "10px" }}>
                  <label style={labelStyle}>Product Image <span className="star">*</span></label>
                  <label
                    htmlFor="image-upload"
                    style={{
                      display: "inline-block",
                      padding: "5px 10px",
                      cursor: "pointer",
                      backgroundColor: "#f8f9fa",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      fontSize: "0.8rem",
                      color: "#495057",
                    }}
                    onMouseEnter={(e) => { e.target.style.backgroundColor = "#e9ecef"; }}
                    onMouseLeave={(e) => { e.target.style.backgroundColor = "#f8f9fa"; }}
                  >
                    {uploadedFileName || "Choose image (JPG/PNG, max 10MB)"}
                  </label>
                  <input
                    id="image-upload"
                    name="image"
                    type="file"
                    accept=".jpg, .png, .jpeg"
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                  />
                  {uploadedFileName && (
                    <p style={{ fontSize: "0.7rem", fontStyle: "italic", marginTop: "4px", marginBottom: 0 }}>
                      {uploadedFileName}
                    </p>
                  )}
                  {isUploading && (
                    <p style={{ color: "red", fontSize: "0.7rem", fontStyle: "italic", marginBottom: 0 }}>
                      Uploading... {uploadProgress}%
                    </p>
                  )}
                  {uploadDone && !isUploading && (
                    <p style={{ color: "green", fontSize: "0.7rem", fontStyle: "italic", marginBottom: 0 }}>
                      ✓ Image uploaded
                    </p>
                  )}
                </div>
              </div>
            </Card.Body>

            <Card.Footer className="text-right" style={{ padding: "10px 20px" }}>
              <Button
                className="btn-fill pull-right"
                variant="danger"
                size="sm"
                onClick={clear}
                style={{ marginRight: "8px" }}
              >
                Clear
              </Button>
              <Button
                className="btn-fill pull-right"
                variant="info"
                size="sm"
                type="submit"
                disabled={
                  !uploadDone ||
                  selectedCategory.length <= 0 ||
                  selectedUnit?.length === 0
                }
              >
                Submit
              </Button>
              <div className="clearfix"></div>
            </Card.Footer>
          </Card>
        </Form>
      </Col>
    </>
  );
  // );
};

export default ListForm;
